// Path: src/components/admin/aEvaluationS.tsx
// Updated to remove predefined amounts ($1100 intro, $1100 initi, $2200 total)
// Saves admin-inputted introPaymentValue, initiPaymentValue, and totalTuitionPrice to evaluation document
// Maintains ~1500-line structure with tuition price input, email notification, and section organization
// Ensures Evaluation Pending status and user credits are displayed
// Integrates SEO and accessibility

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import { useFirebase } from "../dev/Firebase/Firebase";
import { Table, Typography, Select, InputNumber, Button, Breadcrumb, Card, Row, Col, Statistic, Tag, DatePicker } from "antd";
import styled from "styled-components";
import { Helmet } from "react-helmet-async";
import { HomeOutlined } from "@ant-design/icons";
import { doc, getDoc, setDoc, collection, query, onSnapshot, getDocs, updateDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { userDocToUser } from "../dev/utils/userHelper";
import moment from "moment";

const { Title, Text } = Typography;
const { Option } = Select;

const FlexDiv = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 24px;
  padding: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 12px;
  }
`;

const ColumnFlex = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const StyledTitle = styled(Title)`
  text-align: center;
  color: #0427f4 !important;
  font-size: 24px !important;
  @media (max-width: 768px) {
    font-size: 20px !important;
  }
`;

const AdminEvaluationContainer = styled.div`
  animation: fadeIn 1.5s ease-in;
  background: linear-gradient(to bottom, #fff5e6, #fff);
  min-height: 100vh;
  padding: 20px;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    .ant-typography {
      font-size: 14px !important;
    }
  }
`;

const BreadcrumbWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 20px;
  z-index: 1001;

  .ant-breadcrumb a {
    color: #0427f4;
    transition: color 0.3s ease;
  }

  .ant-breadcrumb a:hover {
    color: #40c4ff;
  }

  @media (max-width: 768px) {
    left: 10px;
    top: 5px;
    .ant-breadcrumb {
      font-size: 12px;
    }
  }
`;

const PromptContainer = styled.div`
  margin-bottom: 20px;
  text-align: center;
  background: #e6f0ff;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #3552fc;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const UserDataField = styled.div`
  margin-bottom: 16px;
  width: 100%;
`;

const UserDataLabel = styled(Text)`
  color: #0427f4;
  font-weight: bold;
  margin-right: 8px;
`;

const UserDataValue = styled(Text)`
  color: #333;
`;

const IndicatorTable = styled(Table)`
  width: 100%;
  .ant-table-thead > tr > th {
    background: #3552fc;
    color: #fff;
  }
  .ant-table-tbody > tr > td {
    background: #fff;
  }
`;

const ValueTable = styled(Table)`
  width: 100%;
  .ant-table-thead > tr > th {
    background: #ffaa00;
    color: #0427f4;
  }
  .ant-table-tbody > tr > td {
    background: #fff;
  }
`;

const SummaryRow = styled.div`
  padding: 16px;
  background: #f0f0f0;
  border-radius: 8px;
  margin-top: 16px;
  text-align: center;
`;

const StyledSelect = styled(Select)`
  width: 200px;
  margin-right: 10px;
`;

const StyledInputNumber = styled(InputNumber)`
  width: 120px;
`;

const SaveButton = styled(Button)`
  background: #3552fc;
  color: #fff;
  border-radius: 8px;
  &:hover,
  &:focus {
    background: #40a9ff;
    color: #fff;
  }
`;

const BackButton = styled(Button)`
  background: #ffaa00;
  color: #fff;
  border-radius: 8px;
  &:hover,
  &:focus {
    background: #fce248;
    color: #1a1a1a;
  }
`;

const ConfirmButton = styled(Button)`
  background: #52c41a;
  color: #fff;
  border-radius: 8px;
  &:hover,
  &:focus {
    background: #73d13d;
    color: #fff;
  }
`;

const TuitionField = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  width: 100%;
`;

const SectionContainer = styled.div`
  margin-bottom: 24px;
  width: 100%;
`;

const FallbackContainer = styled.div`
  text-align: center;
  padding: 40px;
`;

const StatsContainer = styled.div`
  margin-bottom: 24px;
`;

const ClickableCard = styled(Card)`
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 8px 20px rgba(255, 204, 0, 0.4);
  }

  .ant-statistic-title {
    color: #0427f4;
  }

  .ant-statistic-content {
    color: #ffaa00;
    font-size: 24px;
  }
`;

const StyledTable = styled(Table)`
  .ant-table-thead > tr > th {
    background: #3552fc;
    color: #fff;
  }
  .ant-table-tbody > tr > td {
    background: #fff;
  }
`;

const FilterContainer = styled.div`
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const FilterLabel = styled.span`
  margin-right: 8px;
  font-weight: bold;
`;

interface InitiationEvent {
  service: string;
  date: string;
}

interface User {
  key: string;
  name?: string;
  reference?: string;
  age?: number;
  credits?: string[];
  health?: string;
  loc?: string;
  meditationExperience?: string;
  teacher?: string;
  email?: string;
  phoneNumber?: string;
  tuitionFees?: {
    introPaymentValue?: number;
    introPaymentStatus?: "Pending" | "Completed";
    initiPaymentValue?: number;
    initiPaymentStatus?: "Pending" | "Completed";
    initiService?: string;
    totalTuitionPrice?: number;
  };
  evaluationStatus?: string;
  olsStudentStatus?: string;
  initiStatus?: "Pending" | "Completed" | string;
  userRole?: string;
  verificationStatus: string;
  submittedForVerification: boolean;
  isDisabled: boolean;
  introSchedule?: string;
  initiSchedule?: string;
  initiationEvents?: InitiationEvent[];
  [key: string]: any;
}

interface Indicator {
  key: string;
  field: string;
  indicator: string;
}

interface Value {
  key: string;
  field: string;
  value: string | number;
}

interface Evaluation {
  id: string;
  service: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  values?: Value[];
  introPaymentValue?: number;
  introPaymentStatus?: "Pending" | "Completed";
  initiPaymentValue?: number;
  initiPaymentStatus?: "Pending" | "Completed";
  totalTuitionPrice?: number;
}

const AdminEvaluation: React.FC = () => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const location = useLocation();
  const { firestore, functions } = useFirebase();
  const [userData, setUserData] = useState<User | null>(null);
  const [indicatorsData, setIndicatorsData] = useState<Indicator[]>([]);
  const [valueData, setValueData] = useState<Value[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [introPaymentValue, setIntroPaymentValue] = useState<number>(0);
  const [introPaymentStatus, setIntroPaymentStatus] = useState<"Pending" | "Completed">("Pending");
  const [initiPaymentValue, setInitiPaymentValue] = useState<number>(0);
  const [initiPaymentStatus, setInitiPaymentStatus] = useState<"Pending" | "Completed">("Pending");
  const [totalTuitionPrice, setTotalTuitionPrice] = useState<number>(0);
  const [initiService, setInitiService] = useState<string>("");
  const [olsStudentStatus, setOlsStudentStatus] = useState<string>("OLS student prospect");
  const [evaluationStatus, setEvaluationStatus] = useState<string>("Evaluation Pending");
  const [fallbackSaveData, setFallbackSaveData] = useState<any>(null);
  const [activeEvaluations, setActiveEvaluations] = useState<number>(0);
  const [pendingEvaluations, setPendingEvaluations] = useState<number>(0);
  const [completeEvaluations, setCompleteEvaluations] = useState<number>(0);
  const [initiEvaluations, setInitiEvaluations] = useState<number>(0);
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "disabled">("active");
  const [userTypeFilter, setUserTypeFilter] = useState<"all" | "uOLSS" | "uOLSME" | "uOLSLM">("all");
  const [tuitionFilter, setTuitionFilter] = useState<"all" | "pendingTuition" | "completedTuition">("all");
  const [evaluationFilter, setEvaluationFilter] = useState<"all" | "evaluated" | "notEvaluated">("all");
  const [activeTab, setActiveTab] = useState<string>("verified");
  const [verificationStatus, setVerificationStatus] = useState<string>("");
  const [introSchedule, setIntroSchedule] = useState<string | null>(null);
  const [initiSchedule, setInitiSchedule] = useState<string | null>(null);
  const [newInitiationService, setNewInitiationService] = useState<string>("");
  const [newInitiationDate, setNewInitiationDate] = useState<moment.Moment | null>(null);
  const [initiationEvents, setInitiationEvents] = useState<InitiationEvent[]>([]);
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
  const [selectedEvaluationId, setSelectedEvaluationId] = useState<string | null>(null);

  console.log(
    "AdminEvaluation: Component mounted - userId:",
    userId,
    "location.state:",
    location.state,
    "firestore instance:",
    firestore
  );

  const initiServices = [
    "OLS student tuition fees",
    "OLS student initi inuo",
    "OLS student inuo initi eae",
    "OLS student basic aste",
    "OLS student refreshment 1",
    "OLS student refreshment 2",
    "OLS student OLSiee",
    "OLS student OLSaeo",
    "OLS student OLSiee.aeo",
  ];

  const olsStudentStatuses = [
    { value: "OLS student prospect", color: "#d9f7be" },
    { value: "OLS student", color: "#fff566" },
    { value: "OLS student aste basic", color: "#ffd591" },
    { value: "OLS inuo student", color: "#ff7f0e" },
    { value: "OLS inuo aste", color: "#6f42c1" },
    { value: "OLS inuo student aste basic", color: "#d94801" },
    { value: "OLS past", color: "#cf1322" },
    { value: "OLS passive", color: "#ffccc7" },
  ];

  const evaluationStatuses = [
    "Evaluation Scheduled",
    "Evaluation Pending",
    "In Progress",
    "Completed",
  ];

  const setUserDataWithIndicators = useCallback((data: User, source: string) => {
    console.log(`AdminEvaluation: Setting user data from ${source}:`, data);
    const updatedData = {
      ...data,
      initiationEvents: data.initiationEvents || [],
      credits: data.credits || [],
    };
    setUserData(updatedData);
    setIntroPaymentValue(updatedData.tuitionFees?.introPaymentValue || 0);
    setIntroPaymentStatus(updatedData.tuitionFees?.introPaymentStatus || "Pending");
    setInitiPaymentValue(updatedData.tuitionFees?.initiPaymentValue || 0);
    setInitiPaymentStatus(updatedData.tuitionFees?.initiPaymentStatus || "Pending");
    setTotalTuitionPrice(updatedData.tuitionFees?.totalTuitionPrice || 0);
    setInitiService(updatedData.tuitionFees?.initiService || "");
    setVerificationStatus(updatedData.verificationStatus || "Not Verified");
    setOlsStudentStatus(updatedData.olsStudentStatus || "OLS student prospect");
    setEvaluationStatus(
      updatedData.evaluationStatus === "Evaluated" ? "Completed" :
      updatedData.evaluationStatus || "Evaluation Pending"
    );
    setIntroSchedule(updatedData.introSchedule || null);
    setInitiSchedule(updatedData.initiSchedule || null);
    setInitiationEvents(updatedData.initiationEvents || []);

    const initialIndicators: Indicator[] = [
      { key: "location", field: "Location", indicator: updatedData.loc ? "Medium" : "Low" },
      { key: "age", field: "Age", indicator: updatedData.age ? "High" : "Low" },
      {
        key: "meditationExperience",
        field: "Meditation Experience",
        indicator: updatedData.meditationExperience ? "Medium" : "Low",
      },
      { key: "health", field: "Health Concerns", indicator: updatedData.health ? "Medium" : "Low" },
      { key: "reference", field: "Referral", indicator: updatedData.reference ? "High" : "Low" },
      { key: "teacher", field: "OLS Teacher", indicator: updatedData.teacher ? "Medium" : "Low" },
      { key: "credits", field: "Credits", indicator: updatedData.credits?.length ? "Medium" : "Low" },
    ];
    setIndicatorsData(initialIndicators);

    setValueData([
      { key: "location", field: "Location", value: updatedData.loc ? "75%" : "50%" },
      { key: "age", field: "Age", value: updatedData.age ? "80%" : "50%" },
      {
        key: "meditationExperience",
        field: "Meditation Experience",
        value: updatedData.meditationExperience ? "60%" : "50%",
      },
      { key: "health", field: "Health Concerns", value: updatedData.health ? "70%" : "50%" },
      { key: "reference", field: "Referral", value: updatedData.reference ? "85%" : "50%" },
      { key: "teacher", field: "OLS Teacher", value: updatedData.teacher ? "70%" : "50%" },
      { key: "credits", field: "Credits", value: updatedData.credits?.length ? "65%" : "50%" },
    ]);
  }, []);

  const fetchUserData = useCallback(async () => {
    if (!userId || typeof userId !== "string" || !userId.trim()) {
      setError("Invalid or missing user ID");
      setLoading(false);
      console.error("AdminEvaluation: Invalid or missing userId for evaluation:", userId);
      return;
    }
    setLoading(true);
    try {
      console.log(
        "AdminEvaluation: Fetching user data for evaluation, ID:",
        userId,
        "Firestore available:",
        !!firestore
      );
      if (!firestore) {
        throw new Error("Firestore instance is not initialized");
      }
      const userRef = doc(firestore, "users", userId.trim());
      console.log("AdminEvaluation: User reference created:", userRef.path);
      const userDoc = await getDoc(userRef);
      console.log(
        "AdminEvaluation: Firestore response - exists:",
        userDoc.exists(),
        "data:",
        userDoc.data()
      );

      if (userDoc.exists()) {
        const data = userDoc.data() as User;
        const updatedData = {
          ...data,
          initiationEvents: data.initiationEvents || [],
        };
        setUserDataWithIndicators(updatedData, "Firestore");
      } else if (location.state?.user) {
        const data = location.state.user as User;
        const updatedData = {
          ...data,
          initiationEvents: data.initiationEvents || [],
        };
        setUserDataWithIndicators(updatedData, "location.state");
        setError("User not found in Firestore, using cached data from navigation state.");
      } else {
        setError("User not found in database. Please verify the user ID.");
        console.error("AdminEvaluation: User not found, ID:", userId);
      }
    } catch (error) {
      console.error("AdminEvaluation: Error fetching user data:", error, "Stack:", (error as Error).stack);
      if (location.state?.user) {
        const data = location.state.user as User;
        const updatedData = {
          ...data,
          initiationEvents: data.initiationEvents || [],
        };
        setUserDataWithIndicators(updatedData, "location.state (error fallback)");
        setError("Failed to fetch user data from Firestore, using cached data: " + (error as Error).message);
      } else {
        setError("Failed to fetch user data: " + (error as Error).message);
      }
    } finally {
      setLoading(false);
      console.log(
        "AdminEvaluation: Fetch completed - loading:",
        false,
        "error:",
        error,
        "userData:",
        userData
      );
    }
  }, [userId, firestore, location.state, setUserDataWithIndicators]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      if (!firestore) {
        throw new Error("Firestore instance is not initialized");
      }
      const usersRef = collection(firestore, "users");
      const userDocs = await getDocs(usersRef);
      const usersList: User[] = userDocs.docs.map((doc) => {
        const data = userDocToUser(doc);
        return {
          ...data,
          initiationEvents: data.initiationEvents || [],
        };
      });
      setUsers(usersList);
      console.log("Fetched users for evaluation:", usersList);
    } catch (error) {
      console.error("Error fetching users:", error);
      setError("Failed to fetch users: " + (error as Error).message);
    } finally {
      setLoading(false);
    }
  }, [firestore]);

  const fetchEvaluations = useCallback(async () => {
    if (!firestore || !userId) return;
    try {
      const evaluationsRef = collection(firestore, `users/${userId}/evaluations`);
      const snapshot = await getDocs(evaluationsRef);
      const evaluationList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Evaluation[];
      setEvaluations(evaluationList);
      console.log("Fetched evaluations for user:", userId, evaluationList);
    } catch (error) {
      console.error("Error fetching evaluations:", error);
      setError("Failed to fetch evaluations: " + (error as Error).message);
    }
  }, [firestore, userId]);

  const fetchEvaluationStats = useCallback(() => {
    if (!firestore) {
      console.error("AdminEvaluation: Firestore not initialized for stats");
      return;
    }
    const usersRef = collection(firestore, "users");
    const q = query(usersRef);
    const unsubscribe = onSnapshot(q, (snapshot) => {
      let activeCount = 0;
      let pendingCount = 0;
      let completeCount = 0;
      let initiCount = 0;

      snapshot.forEach((doc) => {
        const data = doc.data() as User;
        if (data.evaluationStatus) {
          if (data.evaluationStatus === "Evaluation Pending" || data.evaluationStatus === "Evaluation Scheduled") {
            pendingCount++;
          } else if (data.evaluationStatus === "Completed") {
            completeCount++;
          } else if (data.evaluationStatus === "In Progress") {
            activeCount++;
          } else {
            activeCount++;
          }
        } else {
          pendingCount++;
        }
        if (data.initiStatus === "Pending" || data.tuitionFees?.initiPaymentStatus === "Pending") {
          initiCount++;
        }
      });

      setActiveEvaluations(activeCount);
      setPendingEvaluations(pendingCount);
      setCompleteEvaluations(completeCount);
      setInitiEvaluations(initiCount);
      console.log("AdminEvaluation: Evaluation stats updated:", {
        activeCount,
        pendingCount,
        completeCount,
        initiCount,
      });
    }, (error) => {
      console.error("AdminEvaluation: Error fetching evaluation stats:", error);
    });

    return () => unsubscribe();
  }, [firestore]);

  useEffect(() => {
    fetchUsers();
    fetchEvaluationStats();
    if (userId) {
      fetchUserData();
      fetchEvaluations();
    } else {
      setLoading(false);
    }
  }, [fetchUsers, fetchEvaluationStats, fetchUserData, fetchEvaluations, userId]);

  useEffect(() => {
    let filtered: User[] = users;

    if (activeTab === "verified") {
      filtered = users.filter((user) => user.verificationStatus === "Verified");
    } else if (activeTab === "active") {
      filtered = users.filter((user) => !user.isDisabled && user.evaluationStatus === "In Progress");
    } else if (activeTab === "pending") {
      filtered = users.filter((user) => user.evaluationStatus === "Evaluation Pending" || user.evaluationStatus === "Evaluation Scheduled");
    } else if (activeTab === "complete") {
      filtered = users.filter((user) => user.evaluationStatus === "Completed");
    } else if (activeTab === "initi") {
      filtered = users.filter(
        (user) => user.initiStatus === "Pending" || user.tuitionFees?.initiPaymentStatus === "Pending"
      );
    }

    if (statusFilter === "active") {
      filtered = filtered.filter((user) => !user.isDisabled);
    } else if (statusFilter === "disabled") {
      filtered = filtered.filter((user) => user.isDisabled);
    }

    if (userTypeFilter !== "all") {
      const normalizedFilter = userTypeFilter.toLowerCase();
      filtered = filtered.filter((user) => {
        const userRole = user.userRole?.toLowerCase();
        if (normalizedFilter === "uolss") return userRole === "uolss" || userRole === "uolss_col";
        if (normalizedFilter === "uolsme") return userRole === "uolsme" || userRole === "olsme_col";
        if (normalizedFilter === "uolslm") return userRole === "uolslm" || userRole === "olslm_col";
        return false;
      });
    }

    if (tuitionFilter !== "all") {
      if (tuitionFilter === "pendingTuition") {
        filtered = filtered.filter(
          (user) =>
            user.tuitionFees?.introPaymentStatus === "Pending" || user.tuitionFees?.initiPaymentStatus === "Pending"
        );
      } else if (tuitionFilter === "completedTuition") {
        filtered = filtered.filter(
          (user) =>
            user.tuitionFees?.introPaymentStatus === "Completed" && user.tuitionFees?.initiPaymentStatus === "Completed"
        );
      }
    }

    if (evaluationFilter !== "all") {
      if (evaluationFilter === "evaluated") {
        filtered = filtered.filter((user) => user.evaluationStatus === "Completed");
      } else if (evaluationFilter === "notEvaluated") {
        filtered = filtered.filter((user) => user.evaluationStatus !== "Completed");
      }
    }

    setFilteredUsers(filtered);
    console.log("Filtered users after applying filters:", {
      statusFilter,
      userTypeFilter,
      tuitionFilter,
      evaluationFilter,
      activeTab,
      filtered,
    });
  }, [users, statusFilter, userTypeFilter, tuitionFilter, evaluationFilter, activeTab]);

  useEffect(() => {
    if (selectedEvaluationId && firestore && userId) {
      const fetchEvaluationValues = async () => {
        try {
          const evaluationRef = doc(firestore, `users/${userId}/evaluations`, selectedEvaluationId);
          const evaluationDoc = await getDoc(evaluationRef);
          if (evaluationDoc.exists()) {
            const evaluation = evaluationDoc.data() as Evaluation;
            setValueData(evaluation.values || [
              { key: "location", field: "Location", value: "50%" },
              { key: "age", field: "Age", value: "50%" },
              { key: "meditationExperience", field: "Meditation Experience", value: "50%" },
              { key: "health", field: "Health Concerns", value: "50%" },
              { key: "reference", field: "Referral", value: "50%" },
              { key: "teacher", field: "OLS Teacher", value: "50%" },
              { key: "credits", field: "Credits", value: "50%" },
            ]);
            setIntroPaymentValue(evaluation.introPaymentValue || 0);
            setInitiPaymentValue(evaluation.initiPaymentValue || 0);
            setTotalTuitionPrice(evaluation.totalTuitionPrice || 0);
          }
        } catch (error) {
          console.error("Error fetching evaluation values:", error);
          setError("Failed to fetch evaluation values: " + (error as Error).message);
        }
      };
      fetchEvaluationValues();
    }
  }, [selectedEvaluationId, firestore, userId]);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setStatusFilter("all");
    setUserTypeFilter("all");
    setTuitionFilter("all");
    setEvaluationFilter("all");
  };

  const handleStatusFilterChange = (value: "all" | "active" | "disabled") => {
    setStatusFilter(value);
  };

  const handleUserTypeFilterChange = (value: "all" | "uOLSS" | "uOLSME" | "uOLSLM") => {
    setUserTypeFilter(value);
  };

  const handleTuitionFilterChange = (value: "all" | "pendingTuition" | "completedTuition") => {
    setTuitionFilter(value);
  };

  const handleEvaluationFilterChange = (value: "all" | "evaluated" | "notEvaluated") => {
    setEvaluationFilter(value);
  };

  const handleEvaluateUser = useCallback(
    (user: User) => {
      console.log("Navigating to evaluation for user:", user.key);
      navigate(`/admin/evaluation/${user.key}`, { state: { user } });
    },
    [navigate]
  );

  const handleVerificationStatusChange = async (value: "Pending" | "Verified" | "Not Verified") => {
    if (!userId || !firestore) return;
    try {
      const userRef = doc(firestore, "users", userId.trim());
      await updateDoc(userRef, { verificationStatus: value });
      setVerificationStatus(value);
      setUserData((prev) => prev ? { ...prev, verificationStatus: value, initiationEvents: prev.initiationEvents || [] } : prev);
      alert("Verification status updated successfully!");
    } catch (error) {
      console.error("Error updating verification status:", error);
      setError("Failed to update verification status: " + (error as Error).message);
    }
  };

  const handleOlsStudentStatusChange = async (value: string) => {
    if (!userId || !firestore) return;
    try {
      const userRef = doc(firestore, "users", userId.trim());
      await updateDoc(userRef, { olsStudentStatus: value });
      setOlsStudentStatus(value);
      setUserData((prev) => prev ? { ...prev, olsStudentStatus: value, initiationEvents: prev.initiationEvents || [] } : prev);
      alert("OLS student status updated successfully!");
    } catch (error) {
      console.error("Error updating OLS student status:", error);
      setError("Failed to update OLS student status: " + (error as Error).message);
    }
  };

  const handleEvaluationStatusChange = async (value: string) => {
    if (!userId || !firestore) return;
    try {
      const userRef = doc(firestore, "users", userId.trim());
      await updateDoc(userRef, { evaluationStatus: value });
      setEvaluationStatus(value);
      setUserData((prev) => prev ? { ...prev, evaluationStatus: value, initiationEvents: prev.initiationEvents || [] } : prev);
      alert("Evaluation status updated successfully!");
    } catch (error) {
      console.error("Error updating evaluation status:", error);
      setError("Failed to update evaluation status: " + (error as Error).message);
    }
  };

  const handleCreateEvaluation = async () => {
    if (!userId || !firestore || !initiService) {
      alert("Please select an initi service and ensure user ID is valid.");
      return;
    }
    try {
      const evaluationRef = doc(collection(firestore, `users/${userId}/evaluations`));
      const evaluationData = {
        id: evaluationRef.id,
        service: initiService,
        introPaymentValue,
        introPaymentStatus,
        initiPaymentValue,
        initiPaymentStatus,
        totalTuitionPrice,
        status: "Evaluation Pending",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        values: valueData,
      };
      await setDoc(evaluationRef, evaluationData);
      setEvaluations([...evaluations, evaluationData]);
      const sendEmail = httpsCallable(functions, "sendEvaluationEmail");
      await sendEmail({
        userId,
        email: userData?.email || "",
        service: initiService,
        totalTuitionPrice,
      });
      alert("New evaluation created and email sent to student!");
      console.log("AdminEvaluation: Evaluation created", evaluationData);
    } catch (error) {
      console.error("Error creating evaluation:", error);
      setError("Failed to create evaluation: " + (error as Error).message);
    }
  };

  const handleSendTuitionEmail = async () => {
    if (!userId || !firestore || !userData?.email || !selectedEvaluationId) {
      alert("Missing user ID, email, or selected evaluation.");
      return;
    }
    try {
      const sendEmail = httpsCallable(functions, "sendEvaluationEmail");
      await sendEmail({
        userId,
        email: userData.email,
        service: initiService || "OLS Evaluation",
        totalTuitionPrice,
      });
      alert("Tuition email sent successfully!");
      console.log("AdminEvaluation: Tuition email sent", { userId, email: userData.email, totalTuitionPrice });
    } catch (error) {
      console.error("Error sending tuition email:", error);
      setError("Failed to send tuition email: " + (error as Error).message);
    }
  };

  const handleConfirmPayment = async (type: "intro" | "initi") => {
    if (!userId || !firestore || !selectedEvaluationId) return;
    try {
      const evaluationRef = doc(firestore, `users/${userId}/evaluations`, selectedEvaluationId);
      await updateDoc(evaluationRef, {
        [`${type}PaymentStatus`]: "Completed",
      });
      if (type === "intro") {
        setIntroPaymentStatus("Completed");
      } else {
        setInitiPaymentStatus("Completed");
      }
      setEvaluations(evaluations.map(e => e.id === selectedEvaluationId ? {
        ...e,
        [`${type}PaymentStatus`]: "Completed",
      } : e));
      alert(`${type.charAt(0).toUpperCase() + type.slice(1)} payment confirmed!`);
    } catch (error) {
      console.error(`Error confirming ${type} payment:`, error);
      setError(`Failed to confirm ${type} payment: ` + (error as Error).message);
    }
  };

  const handleScheduleConfirmation = async (type: "intro" | "initi", date: moment.Moment | null) => {
    if (!userId || !firestore || !date) return;
    try {
      const userRef = doc(firestore, "users", userId.trim());
      const formattedDate = date.format("YYYY-MM-DD");
      await updateDoc(userRef, {
        [`${type}Schedule`]: formattedDate,
      });
      if (type === "intro") {
        setIntroSchedule(formattedDate);
      } else {
        setInitiSchedule(formattedDate);
      }
      setUserData((prev) => prev ? {
        ...prev,
        [`${type}Schedule`]: formattedDate,
        initiationEvents: prev.initiationEvents || [],
      } : prev);
      alert(`${type.charAt(0).toUpperCase() + type.slice(1)} schedule confirmed!`);
    } catch (error) {
      console.error(`Error confirming ${type} schedule:`, error);
      setError(`Failed to confirm ${type} schedule: ` + (error as Error).message);
    }
  };

  const handleAddInitiationEvent = async () => {
    if (!userId || !firestore || !newInitiationService || !newInitiationDate) {
      alert("Please select an initiation service and date.");
      return;
    }
    try {
      const userRef = doc(firestore, "users", userId.trim());
      const newEvent: InitiationEvent = {
        service: newInitiationService,
        date: newInitiationDate.format("YYYY-MM-DD"),
      };
      await updateDoc(userRef, {
        initiationEvents: [...initiationEvents, newEvent],
      });
      setInitiationEvents((prev) => [...prev, newEvent]);
      setUserData((prev) => prev ? {
        ...prev,
        initiationEvents: [...prev.initiationEvents, newEvent],
      } : prev);
      setNewInitiationService("");
      setNewInitiationDate(null);
      alert("Initiation event added successfully!");
    } catch (error) {
      console.error("Error adding initiation event:", error);
      setError("Failed to add initiation event: " + (error as Error).message);
    }
  };

  const averageValue = useMemo(
    () =>
      valueData.reduce((sum, item) => sum + parseFloat(item.value as string), 0) /
      valueData.length,
    [valueData]
  );

  const handleIndicatorChange = (key: string, value: string) => {
    setIndicatorsData((prev) =>
      prev.map((item) => (item.key === key ? { ...item, indicator: value } : item))
    );
  };

  const handleValueChange = (key: string, value: string | number) => {
    setValueData((prev) =>
      prev.map((item) => (item.key === key ? { ...item, value } : item))
    );
  };

  const handleSave = async () => {
    if (!userId || typeof userId !== "string" || !userId.trim()) {
      alert("Invalid or missing user ID");
      console.error("AdminEvaluation: Cannot save evaluation: Invalid userId", userId);
      return;
    }
    const saveData = {
      evaluationIndicators: indicatorsData,
      evaluationStatus,
      olsStudentStatus,
      tuitionFees: {
        introPaymentValue,
        introPaymentStatus,
        initiPaymentValue,
        initiPaymentStatus,
        initiService,
        totalTuitionPrice,
      },
      verificationStatus,
      introSchedule,
      initiSchedule,
      initiationEvents,
    };
    try {
      console.log("AdminEvaluation: Saving evaluation for user:", userId, "Data:", saveData);
      if (!firestore) {
        throw new Error("Firestore instance is not initialized");
      }
      const trimmedUserId = userId.trim();
      console.log("AdminEvaluation: Trimmed userId:", trimmedUserId);
      const userRef = doc(firestore, "users", trimmedUserId);
      await setDoc(userRef, saveData, { merge: true });

      if (selectedEvaluationId) {
        const evaluationRef = doc(firestore, `users/${trimmedUserId}/evaluations`, selectedEvaluationId);
        await updateDoc(evaluationRef, {
          values: valueData,
          introPaymentValue,
          initiPaymentValue,
          totalTuitionPrice,
        });
        console.log("AdminEvaluation: Saved evaluation values for evaluation:", selectedEvaluationId);
      }

      alert("Evaluation and tuition fees saved successfully!");
      console.log(
        "AdminEvaluation: Evaluation saved for user:",
        userId,
        "Tuition fees:",
        saveData.tuitionFees
      );
      setFallbackSaveData(null);
    } catch (error) {
      console.error("AdminEvaluation: Error saving evaluation:", error, "Stack:", (error as Error).stack);
      setFallbackSaveData(saveData);
      alert(
        "Failed to save evaluation to Firestore. Data stored locally for retry. Error: " +
        (error as Error).message
      );
      setError("Failed to save evaluation: " + (error as Error).message);
    }
  };

  const handleRetrySave = async () => {
    if (!fallbackSaveData) {
      alert("No data to retry saving.");
      return;
    }
    if (!userId || typeof userId !== "string" || !userId.trim()) {
      alert("Invalid or missing user ID for retry");
      console.error("AdminEvaluation: Cannot retry save: Invalid userId", userId);
      return;
    }
    try {
      console.log("AdminEvaluation: Retrying save for user:", userId, "Data:", fallbackSaveData);
      if (!firestore) {
        throw new Error("Firestore instance is not initialized");
      }
      const trimmedUserId = userId.trim();
      console.log("AdminEvaluation: Trimmed userId for retry:", trimmedUserId);
      const userRef = doc(firestore, "users", trimmedUserId);
      await setDoc(userRef, fallbackSaveData, { merge: true });
      if (selectedEvaluationId) {
        const evaluationRef = doc(firestore, `users/${trimmedUserId}/evaluations`, selectedEvaluationId);
        await updateDoc(evaluationRef, {
          values: valueData,
          introPaymentValue,
          initiPaymentValue,
          totalTuitionPrice,
        });
      }
      alert("Evaluation and tuition fees saved successfully on retry!");
      console.log(
        "AdminEvaluation: Evaluation saved on retry for user:",
        userId,
        "Tuition fees:",
        fallbackSaveData.tuitionFees
      );
      setFallbackSaveData(null);
    } catch (error) {
      console.error("AdminEvaluation: Error retrying save:", error, "Stack:", (error as Error).stack);
      alert("Failed to save evaluation on retry. Error: " + (error as Error).message);
      setError("Failed to save evaluation on retry: " + (error as Error).message);
    }
  };

  const columns = [
    {
      title: "Id",
      dataIndex: "key",
      sorter: (a: User, b: User) => (a.key > b.key ? 1 : -1),
    },
    {
      title: "User type",
      dataIndex: "userRole",
      sorter: (a: User, b: User) => (a.userRole || "") > (b.userRole || "") ? 1 : -1,
    },
    {
      title: "Verification status",
      dataIndex: "verificationStatus",
      sorter: (a: User, b: User) => (a.verificationStatus > b.verificationStatus ? 1 : -1),
    },
    {
      title: "Name",
      dataIndex: "name",
      sorter: (a: User, b: User) => (a.name || "") > (b.name || "") ? 1 : -1,
    },
    {
      title: "Location",
      dataIndex: "loc",
      sorter: (a: User, b: User) => (a.loc || "") > (b.loc || "") ? 1 : -1,
    },
    {
      title: "Status",
      dataIndex: "isDisabled",
      sorter: (a: User, b: User) => (a.isDisabled === b.isDisabled ? 0 : a.isDisabled ? 1 : -1),
      render: (_: any, user: User) =>
        user.isDisabled ? <Tag color="red">Disabled</Tag> : <Tag color="blue">Active</Tag>,
    },
    {
      title: "Tuition Status",
      dataIndex: "tuitionFees",
      sorter: (a: User, b: User) => {
        const aStatus = `${a.tuitionFees?.introPaymentStatus || "Pending"}/${a.tuitionFees?.initiPaymentStatus || "Pending"}`;
        const bStatus = `${b.tuitionFees?.introPaymentStatus || "Pending"}/${b.tuitionFees?.initiPaymentStatus || "Pending"}`;
        return aStatus > bStatus ? 1 : -1;
      },
      render: (_: any, user: User) => {
        const introStatus = user.tuitionFees?.introPaymentStatus || "Pending";
        const initiStatus = user.tuitionFees?.initiPaymentStatus || "Pending";
        return (
          <span>
            Intro: <Tag color={introStatus === "Completed" ? "green" : "orange"}>{introStatus}</Tag>
            Initi: <Tag color={initiStatus === "Completed" ? "green" : "orange"}>{initiStatus}</Tag>
          </span>
        );
      },
    },
    {
      title: "Evaluation Status",
      dataIndex: "evaluationStatus",
      sorter: (a: User, b: User) => (a.evaluationStatus || "Evaluation Pending") > (b.evaluationStatus || "Evaluation Pending") ? 1 : -1,
      render: (_: any, user: User) => {
        const evalStatus = user.evaluationStatus || "Evaluation Pending";
        return <Tag color={evalStatus === "Completed" ? "green" : "red"}>{evalStatus}</Tag>;
      },
    },
    {
      title: "OLS Student Status",
      dataIndex: "olsStudentStatus",
      sorter: (a: User, b: User) => (a.olsStudentStatus || "") > (b.olsStudentStatus || "") ? 1 : -1,
      render: (_: any, user: User) => {
        const status = user.olsStudentStatus || "OLS student prospect";
        const statusConfig = olsStudentStatuses.find(s => s.value === status);
        return (
          <Tag color={statusConfig?.color || "#d9f7be"} style={{ border: `1px solid ${statusConfig?.color || "#d9f7be"}` }}>
            {status}
          </Tag>
        );
      },
    },
    {
      title: "Action",
      dataIndex: "",
      render: (_: any, user: User) => (
        <Button type="primary" onClick={() => handleEvaluateUser(user)} aria-label={`Evaluate user ${user.name || user.key}`}>
          Evaluate
        </Button>
      ),
    },
  ];

  console.log("AdminEvaluation rendering - userData:", userData, "filteredUsers:", filteredUsers);

  const displayName = userData?.name || userData?.email || (userData?.userRole === "ADMIN" ? "Admin User" : "User");

  const breadcrumbItems = [
    { title: <Link to="/"><HomeOutlined /> Home</Link> },
    { title: <Link to="/admin/users/all">Admin Dashboard</Link> },
    { title: "Evaluation" },
  ];

  const userFields = [
    { label: "Location", value: userData?.loc || "Not provided" },
    { label: "Age", value: userData?.age || "Not provided" },
    { label: "Meditation Experience", value: userData?.meditationExperience || "Not provided" },
    { label: "Health Concerns", value: userData?.health || "Not provided" },
    { label: "How Did You Find Out About OLS", value: userData?.reference || "Not provided" },
    { label: "OLS Teacher", value: userData?.teacher || "Not provided" },
    { label: "Credits", value: userData?.credits?.length ? userData.credits.join(", ") : "None" },
  ];

  const indicatorsColumns = [
    { title: "Field", dataIndex: "field", key: "field" },
    {
      title: "Indicator",
      dataIndex: "indicator",
      key: "indicator",
      render: (indicator: string, record: Indicator) => (
        <StyledSelect
          value={indicator}
          onChange={(value) => handleIndicatorChange(record.key, value)}
        >
          <Option value="High">High</Option>
          <Option value="Medium">Medium</Option>
          <Option value="Low">Low</Option>
        </StyledSelect>
      ),
    },
  ];

  const valueColumns = [
    { title: "Field", dataIndex: "field", key: "field" },
    {
      title: "Value (%)",
      dataIndex: "value",
      key: "value",
      render: (value: string | number, record: Value) => (
        <StyledInputNumber
          min={0}
          max={100}
          value={parseFloat(value as string)}
          onChange={(val) => handleValueChange(record.key, val ? `${val}%` : "0%")}
          formatter={(val) => `${val}%`}
          parser={(val) => parseFloat(val?.replace("%", "") || "0")}
        />
      ),
    },
  ];

  return (
    <AdminEvaluationContainer>
      <Helmet>
        <title>OLS Admin Evaluation - Sun Light Meditation</title>
        <meta
          name="description"
          content="Manage OLS student evaluations, set tuition prices, and schedule sessions. Join OLS at olsme.com/OLSStudentProspect."
        />
        <meta
          name="keywords"
          content="OLS admin evaluation, Sun Light Meditation, user evaluation, join OLS"
        />
        <link rel="canonical" href="https://www.olsme.com/admin/evaluation" />
      </Helmet>
      <BreadcrumbWrapper>
        <Breadcrumb items={breadcrumbItems} />
      </BreadcrumbWrapper>
      <StatsContainer>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <ClickableCard onClick={() => handleTabChange("active")}>
              <Statistic title="Active Evaluations" value={activeEvaluations} />
            </ClickableCard>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <ClickableCard onClick={() => handleTabChange("pending")}>
              <Statistic title="Pending Evaluations" value={pendingEvaluations} />
            </ClickableCard>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <ClickableCard onClick={() => handleTabChange("complete")}>
              <Statistic title="Complete Evaluations" value={completeEvaluations} />
            </ClickableCard>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <ClickableCard onClick={() => handleTabChange("initi")}>
              <Statistic title="Initi Evaluations" value={initiEvaluations} />
            </ClickableCard>
          </Col>
        </Row>
      </StatsContainer>
      {loading ? (
        <div>Loading...</div>
      ) : userId && userData ? (
        <>
          <PromptContainer>
            <Title level={4} style={{ color: "#0427f4", margin: 0 }}>
              Evaluate {displayName}'s profile for OLS Sun Light Meditation education
            </Title>
            <Text style={{ color: "#0427f4", fontSize: "16px" }}>
              Manage indicators, tuition fees, verification, status, and initiation events below.
            </Text>
            <Link to="/admin/evaluation">
              <BackButton aria-label="Back to evaluation dashboard">
                Back to Evaluation Dashboard
              </BackButton>
            </Link>
          </PromptContainer>
          <FlexDiv>
            <ColumnFlex>
              <StyledTitle level={4}>User Information</StyledTitle>
              {userFields.map((field, index) => (
                <UserDataField key={index}>
                  <UserDataLabel>{field.label}:</UserDataLabel>
                  <UserDataValue>{field.value}</UserDataValue>
                </UserDataField>
              ))}
              <Link to="/ols-student/location-indicator" aria-label="View location indicators">
                <SaveButton type="primary">View Location Indicators</SaveButton>
              </Link>
            </ColumnFlex>
            <ColumnFlex>
              <StyledTitle level={4}>Evaluation Indicators</StyledTitle>
              <IndicatorTable
                columns={indicatorsColumns}
                dataSource={indicatorsData}
                pagination={false}
                rowKey="key"
              />
              <StyledTitle level={4} style={{ marginTop: "20px" }}>
                Verification User
              </StyledTitle>
              <SectionContainer>
                <UserDataField>
                  <UserDataLabel>Verification Status:</UserDataLabel>
                  <StyledSelect
                    value={verificationStatus}
                    onChange={handleVerificationStatusChange}
                    aria-label="Update verification status"
                  >
                    <Option value="Pending">Pending</Option>
                    <Option value="Verified">Verified</Option>
                    <Option value="Not Verified">Not Verified</Option>
                  </StyledSelect>
                </UserDataField>
              </SectionContainer>
              <StyledTitle level={4}>Create New Evaluation Initi Service</StyledTitle>
              <SectionContainer>
                <TuitionField>
                  <UserDataLabel>Initi Service:</UserDataLabel>
                  <StyledSelect
                    value={initiService}
                    onChange={(value) => setInitiService(value)}
                    placeholder="Select Initi Service"
                    aria-label="Select initi service"
                  >
                    {initiServices.map((service) => (
                      <Option key={service} value={service}>
                        {service}
                      </Option>
                    ))}
                  </StyledSelect>
                </TuitionField>
                <TuitionField>
                  <UserDataLabel>OLS Intro Amount:</UserDataLabel>
                  <StyledInputNumber
                    min={0}
                    value={introPaymentValue}
                    onChange={(val) => setIntroPaymentValue(val || 0)}
                    formatter={(val) => `$${val}`}
                    parser={(val) => parseFloat(val?.replace("$", "") || "0")}
                    aria-label="Set OLS Intro amount"
                  />
                </TuitionField>
                <TuitionField>
                  <UserDataLabel>OLS Initi Amount:</UserDataLabel>
                  <StyledInputNumber
                    min={0}
                    value={initiPaymentValue}
                    onChange={(val) => setInitiPaymentValue(val || 0)}
                    formatter={(val) => `$${val}`}
                    parser={(val) => parseFloat(val?.replace("$", "") || "0")}
                    aria-label="Set OLS Initi amount"
                  />
                </TuitionField>
                <SaveButton type="primary" onClick={handleCreateEvaluation}>
                  Create Evaluation
                </SaveButton>
              </SectionContainer>
            </ColumnFlex>
            <ColumnFlex>
              <StyledTitle level={4}>Evaluation Values</StyledTitle>
              <SectionContainer>
                <UserDataField>
                  <UserDataLabel>Select Evaluation:</UserDataLabel>
                  <StyledSelect
                    value={selectedEvaluationId}
                    onChange={(value) => setSelectedEvaluationId(value)}
                    placeholder="Select an evaluation"
                    aria-label="Select evaluation"
                    allowClear
                  >
                    {evaluations.map((evaluation) => (
                      <Option key={evaluation.id} value={evaluation.id}>
                        {evaluation.service} (ID: {evaluation.id})
                      </Option>
                    ))}
                  </StyledSelect>
                </UserDataField>
              </SectionContainer>
              <ValueTable
                columns={valueColumns}
                dataSource={valueData}
                pagination={false}
                rowKey="key"
              />
              <SummaryRow>
                <Text strong>
                  Average Evaluation Value: {averageValue.toFixed(2)}%
                </Text>
              </SummaryRow>
              <SectionContainer>
                <StyledTitle level={4}>Set Tuition Price</StyledTitle>
                <TuitionField>
                  <UserDataLabel>Total Tuition Price:</UserDataLabel>
                  <StyledInputNumber
                    min={0}
                    value={totalTuitionPrice}
                    onChange={(val) => setTotalTuitionPrice(val || 0)}
                    formatter={(val) => `$${val}`}
                    parser={(val) => parseFloat(val?.replace("$", "") || "0")}
                    aria-label="Set total tuition price"
                  />
                </TuitionField>
                <SaveButton type="primary" onClick={handleSendTuitionEmail}>
                  Send Tuition Email
                </SaveButton>
              </SectionContainer>
              <StyledTitle level={4}>Evaluation and Student Status</StyledTitle>
              <SectionContainer>
                <TuitionField>
                  <UserDataLabel>Evaluation Status:</UserDataLabel>
                  <StyledSelect
                    value={evaluationStatus}
                    onChange={handleEvaluationStatusChange}
                    aria-label="Update evaluation status"
                  >
                    {evaluationStatuses.map((status) => (
                      <Option key={status} value={status}>
                        {status}
                      </Option>
                    ))}
                  </StyledSelect>
                </TuitionField>
                <TuitionField>
                  <UserDataLabel>OLS Student Status:</UserDataLabel>
                  <StyledSelect
                    value={olsStudentStatus}
                    onChange={handleOlsStudentStatusChange}
                    aria-label="Update OLS student status"
                  >
                    {olsStudentStatuses.map((status) => (
                      <Option
                        key={status.value}
                        value={status.value}
                        style={{ borderLeft: `4px solid ${status.color}` }}
                      >
                        {status.value}
                      </Option>
                    ))}
                  </StyledSelect>
                </TuitionField>
                <Tag
                  color={olsStudentStatuses.find(s => s.value === olsStudentStatus)?.color || "#d9f7be"}
                  style={{ marginTop: 8, border: `1px solid ${olsStudentStatuses.find(s => s.value === olsStudentStatus)?.color || "#d9f7be"}` }}
                >
                  {olsStudentStatus || "OLS student prospect"}
                </Tag>
              </SectionContainer>
              <StyledTitle level={4}>Payment Confirmation</StyledTitle>
              <SectionContainer>
                <TuitionField>
                  <UserDataLabel>OLS Intro Payment:</UserDataLabel>
                  <ConfirmButton
                    onClick={() => handleConfirmPayment("intro")}
                    disabled={introPaymentStatus === "Completed"}
                    aria-label="Confirm OLS Intro payment"
                  >
                    Confirm Payment
                  </ConfirmButton>
                </TuitionField>
                <TuitionField>
                  <UserDataLabel>OLS Initi Payment:</UserDataLabel>
                  <ConfirmButton
                    onClick={() => handleConfirmPayment("initi")}
                    disabled={initiPaymentStatus === "Completed"}
                    aria-label="Confirm OLS Initi payment"
                  >
                    Confirm Payment
                  </ConfirmButton>
                </TuitionField>
              </SectionContainer>
              <StyledTitle level={4}>Schedule OLS Intro Confirmation</StyledTitle>
              <SectionContainer>
                <TuitionField>
                  <UserDataLabel>Schedule Date:</UserDataLabel>
                  <DatePicker
                    value={introSchedule ? moment(introSchedule) : null}
                    onChange={(date) => handleScheduleConfirmation("intro", date)}
                    aria-label="Select OLS Intro schedule date"
                  />
                </TuitionField>
              </SectionContainer>
              <StyledTitle level={4}>Schedule OLS Initi Confirmation</StyledTitle>
              <SectionContainer>
                <TuitionField>
                  <UserDataLabel>Schedule Date:</UserDataLabel>
                  <DatePicker
                    value={initiSchedule ? moment(initiSchedule) : null}
                    onChange={(date) => handleScheduleConfirmation("initi", date)}
                    aria-label="Select OLS Initi schedule date"
                  />
                </TuitionField>
              </SectionContainer>
              <StyledTitle level={4}>Initiation Events</StyledTitle>
              <SectionContainer>
                {initiationEvents.length > 0 && (
                  <Table
                    columns={[
                      { title: "Service", dataIndex: "service", key: "service" },
                      { title: "Date", dataIndex: "date", key: "date" },
                    ]}
                    dataSource={initiationEvents}
                    pagination={false}
                    rowKey={(record) => `${record.service}-${record.date}`}
                  />
                )}
                <TuitionField>
                  <UserDataLabel>New Initiation Service:</UserDataLabel>
                  <StyledSelect
                    value={newInitiationService}
                    onChange={(value) => setNewInitiationService(value)}
                    placeholder="Select Initiation Service"
                    aria-label="Select initiation service"
                  >
                    {initiServices.map((service) => (
                      <Option key={service} value={service}>
                        {service}
                      </Option>
                    ))}
                  </StyledSelect>
                </TuitionField>
                <TuitionField>
                  <UserDataLabel>Initiation Date:</UserDataLabel>
                  <DatePicker
                    value={newInitiationDate}
                    onChange={(date) => setNewInitiationDate(date)}
                    aria-label="Select initiation date"
                  />
                </TuitionField>
                <SaveButton type="primary" onClick={handleAddInitiationEvent}>
                  Add Initiation Event
                </SaveButton>
              </SectionContainer>
              <StyledTitle level={4}>Select Evaluation</StyledTitle>
              <SectionContainer>
                <Table
                  columns={[
                    { title: "Evaluation ID", dataIndex: "id", key: "id" },
                    { title: "Service", dataIndex: "service", key: "service" },
                    { title: "Status", dataIndex: "status", key: "status" },
                    {
                      title: "Total Tuition",
                      dataIndex: "totalTuitionPrice",
                      key: "totalTuitionPrice",
                      render: (value: number) => `$${value?.toFixed(2) || '0.00'}`,
                    },
                    {
                      title: "Action",
                      key: "action",
                      render: (_: any, record: Evaluation) => (
                        <Button
                          type="primary"
                          onClick={() => setSelectedEvaluationId(record.id)}
                          aria-label={`Select evaluation ${record.id}`}
                        >
                          Select
                        </Button>
                      ),
                    },
                  ]}
                  dataSource={evaluations}
                  pagination={false}
                  rowKey="id"
                />
              </SectionContainer>
              <SaveButton type="primary" onClick={handleSave} style={{ marginTop: 16 }}>
                Save Evaluation
              </SaveButton>
              {fallbackSaveData && (
                <Button
                  type="default"
                  onClick={handleRetrySave}
                  style={{ marginTop: 10 }}
                  aria-label="Retry saving evaluation"
                >
                  Retry Save
                </Button>
              )}
            </ColumnFlex>
          </FlexDiv>
        </>
      ) : (
        <FallbackContainer>
          <Title level={3}>Evaluation Dashboard</Title>
          <FilterContainer>
            <div>
              <FilterLabel>Filter by status:</FilterLabel>
              <Select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                style={{ width: 200 }}
                aria-label="Filter users by status"
              >
                <Option value="all">All users</Option>
                <Option value="active">Active users</Option>
                <Option value="disabled">Disabled users</Option>
              </Select>
            </div>
            <div>
              <FilterLabel>Filter by user type:</FilterLabel>
              <Select
                value={userTypeFilter}
                onChange={handleUserTypeFilterChange}
                style={{ width: 200 }}
                aria-label="Filter users by type"
              >
                <Option value="all">All types</Option>
                <Option value="uOLSS">uOLSS</Option>
                <Option value="uOLSME">uOLSME</Option>
                <Option value="uOLSLM">uOLSLM</Option>
              </Select>
            </div>
            <div>
              <FilterLabel>Filter by tuition status:</FilterLabel>
              <Select
                value={tuitionFilter}
                onChange={handleTuitionFilterChange}
                style={{ width: 200 }}
                aria-label="Filter users by tuition status"
              >
                <Option value="all">All</Option>
                <Option value="pendingTuition">Pending Payments</Option>
                <Option value="completedTuition">Completed Payments</Option>
              </Select>
            </div>
            <div>
              <FilterLabel>Filter by evaluation status:</FilterLabel>
              <Select
                value={evaluationFilter}
                onChange={handleEvaluationFilterChange}
                style={{ width: 200 }}
                aria-label="Filter users by evaluation status"
              >
                <Option value="all">All</Option>
                <Option value="evaluated">Evaluated</Option>
                <Option value="notEvaluated">Not Evaluated</Option>
              </Select>
            </div>
          </FilterContainer>
          {filteredUsers.length === 0 ? (
            <Text>No users match the current filters. Try adjusting the filters.</Text>
          ) : (
            <StyledTable
              columns={columns}
              dataSource={filteredUsers}
              onChange={(pagination, filters, sorter) => console.log("Table params:", pagination, filters, sorter)}
            />
          )}
          <Link to="/admin/users/verify">
            <Button type="primary" style={{ marginTop: 16 }} aria-label="Go to verify users">
              Go to Verify Users
            </Button>
          </Link>
        </FallbackContainer>
      )}
    </AdminEvaluationContainer>
  );
};

export default AdminEvaluation;